// The widget grid (ZENITH.md §18): twelve columns, widgets placed by cell.
// In edit mode a widget moves by dragging its title and resizes by its
// corner, following the pointer while a placeholder shows where it will
// land; overlap is refused rather than resolved, so a drop lands where it
// was dropped or not at all. Arrow keys on the focused title do the same one
// cell at a time (Shift resizes). Outside edit mode none of that is wired:
// widgets hold links and buttons, and a stray drag while reaching for one
// must not rearrange the page.
import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { cellsFromPixels, move, resize, type MinSize, type Placed } from "./geometry";

export const ROW_HEIGHT = 88;
export const GAP = 16;

export interface GridProps<T extends Placed> {
  items: T[];
  columns: number;
  editing: boolean;
  minSize(item: T): MinSize;
  /** Called with the whole arrangement after a move or resize lands. */
  onChange(next: T[]): void;
  title(item: T): string;
  subtitle?(item: T): ReactNode;
  actions?(item: T): ReactNode;
  /** Extra class on the body, e.g. to centre a stat tile. */
  bodyClass?(item: T): string | undefined;
  children(item: T): ReactNode;
}

// colWidth and rowHeight are measured once, when the drag starts: reading the
// grid's box during render would lay out on every pointer move, and a ref is
// not for reading in render (react-hooks/refs).
interface Drag { id: string; kind: "move" | "resize"; startX: number; startY: number; dx: number; dy: number; origin: Placed; preview: Placed[] | null; valid: boolean; colWidth: number; rowHeight: number }

export default function Grid<T extends Placed>({ items, columns, editing, minSize, onChange, title, subtitle, actions, bodyClass, children }: GridProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag>();

  const cell = () => {
    const width = ref.current?.getBoundingClientRect().width ?? 0;
    return { colWidth: (width - GAP * (columns - 1)) / columns + GAP, rowHeight: ROW_HEIGHT + GAP };
  };
  const apply = (id: string, kind: Drag["kind"], dc: number, dr: number, origin: Placed): Placed[] | null => {
    const it = items.find((i) => i.id === id);
    if (!it) return null;
    return kind === "move"
      ? move(items, id, origin.x + dc, origin.y + dr, minSize(it), columns)
      : resize(items, id, origin.w + dc, origin.h + dr, minSize(it), columns);
  };

  const start = (kind: Drag["kind"]) => (e: PointerEvent<HTMLElement>, it: T) => {
    if (!editing || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ id: it.id, kind, startX: e.clientX, startY: e.clientY, dx: 0, dy: 0, origin: { ...it }, preview: null, valid: true, ...cell() });
  };
  const during = (e: PointerEvent<HTMLElement>) => {
    if (!drag) return;
    const { colWidth, rowHeight } = drag;
    const dx = e.clientX - drag.startX, dy = e.clientY - drag.startY;
    const { dc, dr } = cellsFromPixels(dx, dy, colWidth, rowHeight);
    const next = apply(drag.id, drag.kind, dc, dr, drag.origin);
    setDrag({ ...drag, dx, dy, preview: next ?? drag.preview, valid: next !== null });
  };
  const end = (e: PointerEvent<HTMLElement>) => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (drag.preview) onChange(merge(items, drag.preview));
    setDrag(undefined);
  };
  const keyed = (e: KeyboardEvent<HTMLElement>, it: T) => {
    if (!editing) return;
    const step: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const d = step[e.key];
    if (!d) return;
    e.preventDefault();
    const next = apply(it.id, e.shiftKey ? "resize" : "move", d[0], d[1], it);
    if (next) onChange(merge(items, next));
  };

  // The dragged widget follows the pointer in pixels; the placeholder sits at
  // the cell it would snap to. Everything else stays put.
  const ghost = drag?.preview?.find((p) => p.id === drag.id);
  const liveStyle = (it: T): CSSProperties | undefined => {
    if (!drag || drag.id !== it.id) return undefined;
    if (drag.kind === "move") return { transform: `translate(${drag.dx}px, ${drag.dy}px)` };
    const { colWidth, rowHeight } = drag;
    return { width: Math.max(colWidth - GAP, it.w * colWidth - GAP + drag.dx), height: Math.max(ROW_HEIGHT, it.h * rowHeight - GAP + drag.dy) };
  };
  const area = (r: Placed) => ({ gridColumn: `${r.x + 1} / span ${r.w}`, gridRow: `${r.y + 1} / span ${r.h}` });

  return (
    <div className={"grid" + (editing ? " grid-editing" : "")} ref={ref} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gridAutoRows: `${ROW_HEIGHT}px`, gap: GAP }}
      onPointerMove={editing ? during : undefined} onPointerUp={editing ? end : undefined} onPointerCancel={editing ? end : undefined}>
      {ghost && <div className={"grid-placeholder" + (drag?.valid ? "" : " grid-placeholder-blocked")} style={area(ghost)} aria-hidden="true" />}
      {items.map((it) => (
        <section key={it.id} className={"widget" + (drag?.id === it.id ? " widget-dragging" + (drag.valid ? "" : " widget-blocked") : "")}
          style={{ ...area(it), ...liveStyle(it) }} aria-label={title(it)}>
          <header className="widget-head">
            {/* In edit mode the title is the handle: it takes focus, the pointer
                and the arrow keys, so it is a button then and a heading otherwise.
                The card keeps its name either way through the section's label. */}
            {editing
              ? <div className="widget-title" role="button" tabIndex={0} title="Drag to move; arrow keys move, Shift+arrows resize"
                  onPointerDown={(e) => start("move")(e, it)} onKeyDown={(e) => keyed(e, it)}>{title(it)}</div>
              : <h3 className="widget-title">{title(it)}</h3>}
            {subtitle && <span className="widget-sub">{subtitle(it)}</span>}
            {editing && actions && <span className="widget-actions">{actions(it)}</span>}
          </header>
          <div className={"widget-body" + (bodyClass?.(it) ? " " + bodyClass(it) : "")}>{children(it)}</div>
          {editing && <span className="widget-resize" aria-hidden="true" onPointerDown={(e) => start("resize")(e, it)} />}
        </section>
      ))}
    </div>
  );
}

/** Applies placed geometry back onto the typed items, keeping their other fields. */
function merge<T extends Placed>(items: T[], placed: Placed[]): T[] {
  return items.map((it) => {
    const p = placed.find((q) => q.id === it.id);
    return p ? { ...it, x: p.x, y: p.y, w: p.w, h: p.h } : it;
  });
}
